import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Share2 } from 'lucide-react';
import { getMySharedSessions, hideSharedSession, deleteSharedSession, downloadSharedSessionFull } from '../api';
import SharedSessionCard from '../components/SharedSessionCard';
import { useOrg } from '../contexts/OrgContext';
import { downloadJson } from '../utils/downloadJson';

export default function SharedSessions() {
  const { active } = useOrg();
  const orgId = active?.org_id;
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);

  // A slow response for an org that's since been switched away from can
  // resolve after a faster, current one — only commit if this is still the
  // most recently issued call, so a stale response can't overwrite it.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const r = await getMySharedSessions();
      if (seq !== requestSeq.current) return;
      setMine(r.data.sessions || []);
      setMsg(null);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      // Surfaced, not swallowed — otherwise a failed load is indistinguishable
      // from "you haven't shared anything."
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Failed to load your shared sessions' });
    } finally {
      // Gated the same way as the commits above — an unconditional
      // `setLoading(false)` here would clear the loading state for a NEWER,
      // still-pending request (e.g. two org switches in a row) as soon as
      // this older call settles, briefly revealing the previous org's list
      // before the current one has actually loaded.
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  // `orgId` isn't read inside `load` (scoped via the `X-Org-Id` header), but
  // switching orgs must still trigger a refetch — otherwise this org's stale
  // list stays on screen after the header switcher changes it.
  useEffect(() => {
    setLoading(true);
    load();
  }, [load, orgId]);

  const withBusy = async (regId, fn) => {
    setBusyId(regId); setMsg(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Action failed' });
    } finally { setBusyId(null); }
  };

  const handleHide = (s) => withBusy(s.reg_id, () => hideSharedSession(s.reg_id, !s.hidden_by_admin));
  const handleDelete = (s) => {
    if (!window.confirm(`Delete "${s.title || 'this shared session'}"? This can't be undone.`)) return;
    return withBusy(s.reg_id, () => deleteSharedSession(s.reg_id));
  };
  const handleDownload = async (s) => {
    try {
      const r = await downloadSharedSessionFull(s.reg_id);
      downloadJson(`${s.reg_id}.json`, r.data.full_json);
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Download failed' });
    }
  };

  if (loading) return <div className="text-center text-fg-muted py-16">Loading…</div>;

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
          <Share2 className="w-6 h-6 text-orange" /> Shared Sessions
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          Push a session with <code className="text-xs bg-field px-1 py-0.5 rounded">/push-to-registry</code> to share it
          with your org — PII, secrets, and local paths are stripped first. Teammates pull it as context with{' '}
          <code className="text-xs bg-field px-1 py-0.5 rounded">@sync:reg_&lt;id&gt;</code>.
        </p>
      </div>

      {msg && (
        <div className="mb-4 p-3 rounded-lg border text-sm bg-red-500/10 border-red-500/25 text-red-400">
          {msg.text}
        </div>
      )}

      {mine.length === 0 ? (
        // A failed load already has its own message in the error banner
        // above — showing "nothing shared yet" underneath it would read as
        // two conflicting explanations for the same empty list.
        msg?.kind !== 'err' && (
          <div className="bg-panel border border-panel-border rounded-xl p-8 text-center">
            <p className="text-sm text-fg-muted">You haven't shared any sessions yet.</p>
            <p className="text-xs text-fg-muted mt-1">
              Run <code className="text-xs bg-field px-1 py-0.5 rounded">/push-to-registry</code> from a latentcode session to share it here.
            </p>
          </div>
        )
      ) : (
        <div className="space-y-3">
          {mine.map((s) => (
            <SharedSessionCard key={s.reg_id} s={s} busy={busyId === s.reg_id} canModerate={false} showOwnBadge={false}
              onHide={handleHide} onDelete={handleDelete} onDownload={handleDownload} />
          ))}
        </div>
      )}
    </div>
  );
}
